package org.catharsis.spendtel.feature.presentation.financialgoal

import org.catharsis.spendtel.core.api.AnalyticsApi
import org.catharsis.spendtel.core.api.AuthenticationRepo
import org.catharsis.spendtel.core.api.SpendTelRepo
import org.catharsis.spendtel.core.arch.MviViewModel
import org.catharsis.spendtel.core.arch.ViewEffect
import org.catharsis.spendtel.core.arch.ViewEvent
import org.catharsis.spendtel.core.arch.ViewState
import org.catharsis.spendtel.feature.data.EventName
import org.catharsis.spendtel.feature.data.FinancialGoal

class FinancialGoalViewModel(
    private val authenticationRepo: AuthenticationRepo,
    private val spendTelRepo: SpendTelRepo,
    private val analytics: AnalyticsApi,
    private val isEditMode: Boolean = false,
) : MviViewModel<FinancialGoalEvent, FinancialGoalState, FinancialGoalEffect>(
    initialState = FinancialGoalState(isEditMode = isEditMode),
) {
    init {
        launch {
            val user = authenticationRepo.getCurrentUser()
            val existingGoal = FinancialGoal.fromId(user?.financialGoal)
            if (existingGoal != null) {
                setState { copy(selectedGoal = existingGoal) }
            }
        }
    }

    override fun receiveEvent(event: FinancialGoalEvent) {
        when (event) {
            is FinancialGoalEvent.OnSelectGoal -> {
                setState { copy(selectedGoal = event.goal, errorMessage = null) }
            }
            FinancialGoalEvent.OnContinue -> saveSelectedGoal()
        }
    }

    private fun saveSelectedGoal() {
        val selectedGoal = latestState.selectedGoal
        if (selectedGoal == null) {
            setState { copy(errorMessage = "Choose the goal that fits you best.") }
            return
        }

        launch {
            setState { copy(isSaving = true, errorMessage = null) }

            val user = authenticationRepo.getCurrentUser()
            if (user == null) {
                setState {
                    copy(
                        isSaving = false,
                        errorMessage = "You must be signed in to continue.",
                    )
                }
                return@launch
            }

            val previousGoalId = user.financialGoal
            spendTelRepo.updateFinancialGoal(user, selectedGoal.id)
                .onSuccess {
                    val eventName = if (isEditMode || !previousGoalId.isNullOrBlank()) {
                        EventName.CHANGED_FINANCIAL_GOAL
                    } else {
                        EventName.SELECTED_FINANCIAL_GOAL
                    }
                    analytics.logClickEvent(
                        eventName,
                        mapOf(
                            "goal_id" to selectedGoal.id,
                            "previous_goal_id" to previousGoalId.orEmpty(),
                        ),
                    )
                    setState { copy(isSaving = false) }
                    if (isEditMode) {
                        emitEffect { FinancialGoalEffect.NavigateBack }
                    } else {
                        emitEffect { FinancialGoalEffect.NavigateHome }
                    }
                }
                .onFailure { error ->
                    setState {
                        copy(
                            isSaving = false,
                            errorMessage = error.message ?: "Could not save your goal. Please try again.",
                        )
                    }
                }
        }
    }
}

data class FinancialGoalState(
    val selectedGoal: FinancialGoal? = null,
    val isSaving: Boolean = false,
    val errorMessage: String? = null,
    val isEditMode: Boolean = false,
) : ViewState

sealed class FinancialGoalEvent : ViewEvent {
    data class OnSelectGoal(val goal: FinancialGoal) : FinancialGoalEvent()
    data object OnContinue : FinancialGoalEvent()
}

sealed class FinancialGoalEffect : ViewEffect {
    data object NavigateHome : FinancialGoalEffect()
    data object NavigateBack : FinancialGoalEffect()
}
